import { useEffect, useState } from 'react'
import type { EventPageClass, EventPageEvent, SignupState } from '../data/types'
import { truthy } from '../data/types'
import { postAddClassEntry, postUpdateClassEntryVehicle } from '../data/eventPageApi'
import { formatMoney } from '../data/format'
import styles from './VehicleSignupModal.module.css'

/** Display-only preview of what this entry will cost - mirrors eventPage.cfc's
 *  computeClassTierAmount() exactly, but the server is authoritative; a mismatch here is only a
 *  cosmetic nuisance; addClassEntry always recomputes it fresh. Only used in 'add' mode - a
 *  'change' (vehicle swap on an existing entry) shows the already-locked-in amountDue instead,
 *  since changing vehicles never re-prices the entry. */
function previewTierAmount(cls: EventPageClass, signup: SignupState): number {
  const override = Number(cls.priceOverride) || 0
  if (override !== 0) return override
  const n = signup.totalEntries
  if (n === 0) return Number(signup.classCost1) || 0
  if (n === 1) return Number(signup.classCost2) || 0
  if (n === 2) return Number(signup.classCost3) || 0
  const cost4 = Number(signup.classCost4) || 0
  return cost4 !== 0 ? cost4 : Number(signup.classCost3) || 0
}

export function VehicleSignupModal({
  cls,
  event,
  signup,
  mode = 'add',
  currentVehicleId,
  currentAmountDue,
  onClose,
  onSaved,
}: {
  cls: EventPageClass
  event: EventPageEvent
  signup: SignupState
  /** 'add' - a fresh entry (the checkbox was just checked). 'change' - swapping the vehicle on an
   *  already-entered class (the racer clicked their "Car" pill). */
  mode?: 'add' | 'change'
  currentVehicleId?: number
  currentAmountDue?: number | string
  onClose: () => void
  onSaved: (next: SignupState) => void
}) {
  const [vehicleId, setVehicleId] = useState<number | ''>(currentVehicleId ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, saving])

  const price = mode === 'change' ? Number(currentAmountDue) || 0 : previewTierAmount(cls, signup)
  const requiresTransponder = truthy(signup.personalOnly)

  const confirm = () => {
    if (!vehicleId) return
    setSaving(true)
    setError(null)
    const action = mode === 'change' ? postUpdateClassEntryVehicle(event.id, cls.id, vehicleId) : postAddClassEntry(event.id, cls.id, vehicleId)
    action.then((next) => onSaved(next)).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : String(err))
      setSaving(false)
    })
  }

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`${mode === 'change' ? 'Change your vehicle' : 'Select your vehicle'} - ${cls.name}`}
      onClick={() => !saving && onClose()}
    >
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close" disabled={saving}>
          ×
        </button>
        <div className={styles.title}>{mode === 'change' ? 'Change your vehicle' : 'Select your vehicle'}</div>
        <div className={styles.className}>{cls.name}</div>
        {price > 0 ? <div className={styles.price}>{formatMoney(price, event.dollarType || 'USD')}</div> : null}

        {requiresTransponder ? (
          <div className={styles.notice}>This event requires a personal transponder - vehicles without one are greyed out below.</div>
        ) : null}

        {signup.vehicles.length ? (
          <div className={styles.vehicles}>
            {signup.vehicles.map((v) => {
              const disabled = requiresTransponder && !truthy(v.hasTransponder)
              return (
                <label key={v.id} className={`${styles.vehicleRow} ${disabled ? styles.vehicleRowDisabled : ''}`}>
                  <input
                    type="radio"
                    name="vehicle"
                    value={v.id}
                    disabled={disabled || saving}
                    checked={vehicleId === v.id}
                    onChange={() => setVehicleId(v.id)}
                  />
                  <span>
                    {v.name}
                    {disabled ? ' - no transponder on file' : ''}
                  </span>
                </label>
              )
            })}
          </div>
        ) : (
          <div className={styles.empty}>
            You don't have any vehicles yet. <a href="/racers/profile/addEditVehicleProfile.cfm">Add one to your profile</a>, then come back here.
          </div>
        )}

        {error ? <div className={styles.error}>{error}</div> : null}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className={styles.confirm} onClick={confirm} disabled={!vehicleId || saving || vehicleId === currentVehicleId}>
            {saving ? 'Saving…' : mode === 'change' ? 'Save vehicle' : 'Add entry'}
          </button>
        </div>
      </div>
    </div>
  )
}
